import React, { forwardRef, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { twMerge } from "tailwind-merge";
import FieldShell, { fieldSurface, FOCUS_RING_ONLY } from './FieldShell';
import { containsEmoji, containsDisallowedCharacters } from '../../../utils/validation/validators';
import { removeEmojis, removeSpecialSymbols } from '../../../utils/validation/sanitizers';

interface InputProps {
  type: string;
  id?: string;
  name?: string;
  label?: string;
  placeholder?: string;
  value?: string | number;
  defaultValue?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  min?: number;
  required?: boolean;
  className?: string;
  error?: string | boolean;
  helperText?: string | false | undefined;
  disabled?: boolean;
  description?: string;
  italicTxt?: string;
  pattern?: string;
  icon?: React.ReactNode;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  infoText?: string;
  infoTrigger?: 'hover' | 'click';
  allowEmojis?: boolean;
  sanitizeEmojis?: boolean;
  autoFocus?: boolean;
  /** Numeric keypad on mobile for a text input (money fields). */
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  autoComplete?: string;
}

export function cn(...inputs: any[]) {
  return twMerge(clsx(inputs));
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type,
      id,
      name,
      label,
      placeholder,
      value,
      defaultValue,
      onChange,
      onBlur,
      onKeyDown,
      min,
      required,
      className,
      error,
      helperText,
      disabled,
      italicTxt,
      description,
      pattern,
      icon,
      prefix,
      suffix,
      infoText,
      infoTrigger = 'hover',
      allowEmojis = false,
      sanitizeEmojis = true,
      autoFocus = false,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [openInfo, setOpenInfo] = useState(false);
    const [emojiError, setEmojiError] = useState<string>();
    const hasInfo = Boolean(infoText);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let newValue = e.target.value;

      // Check for emojis if not allowed
      if (!allowEmojis && sanitizeEmojis && newValue) {
        if (containsEmoji(newValue)) {
          setEmojiError(t('Emojis are not allowed in this field'));
          // Auto-remove emojis
          newValue = removeEmojis(newValue);
        } else if (containsDisallowedCharacters(newValue)) {
          setEmojiError(t('Invalid characters detected'));
          newValue = removeSpecialSymbols(newValue);
        } else {
          setEmojiError(undefined);
        }

        // Update the input value to the sanitized version
        e.target.value = newValue;
      }

      // Call the original onChange handler
      if (onChange) {
        onChange(e);
      }
    };

    const toggleClick = () => {
      if (infoTrigger === 'click') setOpenInfo((o) => !o);
    };
    const openHover = () => {
      if (infoTrigger === 'hover') setOpenInfo(true);
    };
    const closeHover = () => {
      if (infoTrigger === 'hover') setOpenInfo(false);
    };

    const infoButton = hasInfo ? (
      <div
        className="relative inline-flex items-center"
        onMouseEnter={openHover}
        onMouseLeave={closeHover}
      >
        <button
          type="button"
          onClick={toggleClick}
          aria-label={t('Info')}
          className={clsx(
            'ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-hairline text-[10px] leading-none',
            'text-fg-muted hover:bg-surface-soft transition-colors',
            FOCUS_RING_ONLY
          )}
        >
          i
        </button>

        {openInfo && (
          <div
            className="absolute z-50 mt-2 w-64 rounded-md glass p-3 text-xs text-fg-body shadow-lg"
            role="tooltip"
          >
            {t(infoText!)}
          </div>
        )}
      </div>
    ) : null;

    // Three shapes reach this row. The emoji sanitizer's own message wins; then a
    // string `error` (which previously set the ring but printed nothing); then the
    // boolean-`error` + `helperText` pair that BaseForm passes.
    const errorMessage =
      emojiError ||
      (typeof error === 'string' && error ? error : undefined) ||
      (error ? (helperText as string | undefined) : undefined);

    return (
      <FieldShell
        id={id}
        label={label}
        required={required}
        description={description}
        italicText={italicTxt}
        error={errorMessage}
        labelSuffix={infoButton}
      >
        <div className="relative">
          {prefix && (
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-fg-muted">
              {prefix}
            </span>
          )}
          <input
            type={type}
            id={id}
            name={name}
            ref={ref}
            aria-invalid={error || emojiError ? true : undefined}
            // cn() so a caller's `className` (widths, custom heights) still wins
            // over fieldSurface()'s defaults.
            className={cn(
              fieldSurface({ error: !!(error || emojiError), disabled }),
              'block px-3 py-1.5',
              prefix && 'pl-8',
              suffix ? 'pr-10' : 'pr-3',
              className
            )}
            placeholder={placeholder}
            value={value}
            defaultValue={defaultValue}
            onChange={handleChange}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            min={min}
            required={required}
            disabled={disabled}
            pattern={pattern}
            autoFocus={autoFocus}
            {...rest}
          />
          {suffix && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-fg-muted">
              {suffix}
            </span>
          )}
        </div>
      </FieldShell>
    );
  }
);

Input.displayName = 'Input';

export default Input;
